import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useLocalStorage } from '@vueuse/core';
import { useCompanyStore } from '@/modules/company/store';
import { showToast } from '@/utilities/toast-handler';
import { authApi } from '@/utilities/axios';
import { resetUnauthenticatedFlag } from '../../../utilities/axios';
import { UserPermission } from '../../permission/enum';
import axios from 'axios';

const AUTH_URL = process.env.VUE_APP_API_VERSION + '/login';
const FORGOT_PASSWORD_URL = process.env.VUE_APP_API_VERSION + '/users/forgot-password';
const PASSWORD_RESET_URL = 'v2/admin/users/password-reset';
const SSO_CALLBACK = process.env.VUE_APP_API_URL + '/auth/callback';


export const useAuthStore = defineStore('auth', () => {
    const router = useRouter();
    const companyStore = useCompanyStore();
    const loggedUser = ref(useLocalStorage('loggedUser', {}));
    const isGodUser = ref(useLocalStorage('isGodUser', false));
    const token = ref(useLocalStorage('token', null));
    const permissionList = ref(useLocalStorage('permissionList', []));
    const productList = ref(useLocalStorage('productList', []));
    const selectedProduct = ref(useLocalStorage('selectedProduct', null));
    const ttl = ref(useLocalStorage('ttl', null));
    const timer = ref(useLocalStorage('timer', null));
    // states for session timeout
    const lastActive = ref(null);
    const idleTimeout = process.env.VUE_APP_IDLE_CHECK_INTERVAL * 60 * 1000;

    // getters
    const isLoggedIn = computed(() => {
        return loggedUser.value && token.value ? true : false;
    });

    const loggedUserId = computed(() => {
        return loggedUser.value.id;
    });

    const getLoggedUserCompanyId = computed(() => {
        return loggedUser.value?.company_id;
    });

    const getLoggedUserName = computed(() => {
        return loggedUser.value?.name == 'Root' ? 'System Admin' : `${loggedUser.value?.name} ${loggedUser.value?.surname}`;
    });

    const getProductDetails = computed(() => {
        return selectedProduct.value ? productList.value?.find((product) => product.code === selectedProduct.value) : null;
    });

    const getUserPermissions = computed(() => {
        if (permissionList.value) {
            const permissions = typeof permissionList.value === 'object' ? Object.values(permissionList.value) : permissionList.value;
            return permissions?.map((permission) => permission.code);
        }
        return [];
    });

    // Check if logged user is part of global_task_force
    const userIsGlobalTaskForce = computed(() => {
        return hasPermission(UserPermission.GLOBAL_TASK_FORCE);
    });

    // this function checks if the logged user has the required permission or not and returns a boolean value
    const hasPermission = (requiredPermission) => {
        return requiredPermission ? getUserPermissions.value?.includes(requiredPermission) : false;
    };

    // actions
    const login = async (body) => {
        try {
            const response = await authApi.post(AUTH_URL, body);
            // if OTP is enabled for that user then the status will be 202
            if (response.statusCode === 202) {
                showToast({ severity: 'info', summary: 'OTP Sent' }, 'Enter OTP to continue');
                return { otp: true };
            }
            // if response status is
            else if (response.statusCode === 200) {
                resetUnauthenticatedFlag();
                // after successful login setup user data
                await setupLoggedUser(response.data);
            }
            return response;
        } catch (err) {
            // back validation for email and password errors
            if (err.status === 400) {
                throw err?.data?.data;
            }
            // if email and password is valid but incorrect
            else if (err.status == 401) {
                // toast.add({ severity: 'error', summary: 'Error', detail: 'Unauthorized Credentials', life: 5000 });
                showToast({}, 'Invalid Credentials');
                return {};
            }
        }
    };

    const logout = async () => {
        console.log('logout');
        loggedUser.value = {};
        isGodUser.value = false;
        token.value = null;
        ttl.value = null;
        timer.value = null;
        permissionList.value = [];
        productList.value = [];
        selectedProduct.value = null;

        companyStore.activeCompany.value = {};

        await router.push('/')
    };

    const forgotPassword = async (email) => {
        try {
            const response = await authApi.get(FORGOT_PASSWORD_URL, {
                params: { email },
            });
            if (response.statusCode === 200) {
                await showToast(response.toast, 'Password Reset link have been emailed').then(() => {
                    setTimeout(() => {  router.push({ name: 'Login' }); }, 1000);
                });
            }
        } catch (err) {
            // back validation for email and password errors
            if (err.status === 400 || err.status === 401) {
                showToast({}, 'Invalid Email');
            }
        }
    };

    const resetPassword = async (body) => {
        return await authApi
            .post(PASSWORD_RESET_URL, body)
            .then(async (res) => {
                let userData = res.data;
                if (res.statusCode == 200 && userData.token) {
                    // after successful login setup user data
                    await setupLoggedUser(userData);
                } else {
                    showToast({}, 'Failed to reset password');
                }
            })
            .catch((error) => {
                showToast({}, error?.data?.message ? 'Token Invalid' : 'Failed to reset password');
            });
    };

    const selectProduct = (productCode) => {
        selectedProduct.value = productCode;
        showToast({ severity: 'success', summary: 'Success' }, `${productCode} Selected`);
    };

    const selectCompany = (companyId) => {
        loggedUser.value.company_id = companyId;
        showToast({ severity: 'success', summary: 'Success' }, 'Company Selected');
    };

    // updates active state for user session everytime an event is triggered in the App.vue
    const setLastActive = (time) => {
        lastActive.value = time;
    };
    // helper function
    // setups user details
    // if user doesnt have a valid license then log off the user accordingly
    function setupLoggedUser(data) {
        // check if logged user has company id or no, if no then its a god user
        if (!data.user.company_id) {
            isGodUser.value = true;
        }
        loggedUser.value = data.user;
        token.value = data.token;
        ttl.value = data.ttl;
        permissionList.value = data.permissionList;
        productList.value = data.productList;
        // check if user have enough products i.e valid licenses, if not logout that user
        if (productList.value?.length === 0) {
            showToast({ severity: 'info', summary: 'Invalid License' }, 'No valid licenses found for user');
            setTimeout(() => {
                logout();
            }, 2000);
        } else {
            // trigger token refresh timer
            setTokenRefreshTimer();
            // trigger session timeout timer
            startIdleTimeoutChecker();
            // send to an intermediate page of product selection
            router.push({ name: 'ProductSelect' });
        }
    }
    function setTokenRefreshTimer() {
        timer.value = setTimeout(() => {
            // refresh the token with updated token
            // call refreshToken function here
        }, (ttl.value - 1) * 60 * 1000); //multiply by 60 * 1000  (seconds * milliseconds)
    }
    function cleanCache() {
        companyStore.cleanCache();
        window.location.reload();
    }
    // for idle timer check

    function startIdleTimeoutChecker() {
        let intervalId = setInterval(() => {
            let timeDiff = lastActive.value && Date.now() - lastActive.value;
            let timeOut = idleTimeout;
            if (timeDiff > timeOut) {
                logout();
                clearInterval(intervalId);
                // redirect to login if session timeout
                router.push({ name: 'Login' });
            }
        }, idleTimeout); //checks every 10 minutes
    }

   const callbackSSO = async(authCode, state, session_state)  =>{
        try {
            let callbcakUrl = `${SSO_CALLBACK}?code=${authCode}&state=${state}&session_state=${session_state}`;
            const response = await axios.get(callbcakUrl);
            if (response.status === 200) {
                resetUnauthenticatedFlag();
                await setupLoggedUser(response.data.data);
            }
            return response;
        } catch (error) {
            showToast({}, 'SSO authentication failed.');
            return { success: false };
        }
    }

    return {
        // states
        loggedUser,
        isGodUser,
        token,
        ttl,
        timer,
        permissionList,
        productList,
        selectedProduct,
        // getters
        isLoggedIn,
        loggedUserId,
        getLoggedUserCompanyId,
        getLoggedUserName,
        getProductDetails,
        getUserPermissions,
        userIsGlobalTaskForce,
        // actions
        hasPermission,
        login,
        logout,
        callbackSSO,
        forgotPassword,
        resetPassword,
        selectProduct,
        selectCompany,
        setLastActive,
        // helpers
        cleanCache,
    };
});
